import { FieldGroup, PageLoader } from '@ver-uds/react';
import { Alert, Checkbox, Form, TextInputControl } from '@ver-uds/uswds-react';
import React, { KeyboardEvent, KeyboardEventHandler, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Application, CheckEmailRequest, CheckEmailStatus } from '@vis-auth/vis-user-client';
import EnrollmentStepperButtons from '../EnrollmentStepperButtons';
import EVerifyUser from './data/EVerifyUser';
import AlertConstants from '../../../constants/AlertConstants';
import { useMfaApi } from '../../../services';
import { useEnrollment } from '../../../context/Enrollment/EnrollmentContext';
import ExistingEverifyUserModal from './ExistingEverifyUserModal';

export type FormValues = {
  adminUser: EVerifyUser;
  mouSignatoryIsSame: boolean;
  mouSignatory: EVerifyUser;
};

export interface EVerifyUsersFormMFAProps {
  isCorporateAdmin: boolean;
  usersData: EVerifyUser[];
  signatoryIsSame: boolean;
  onNext: () => void;
  onSubmit: (paUser: EVerifyUser, mouUser: EVerifyUser, mouSignatoryIsSame: boolean) => void;
}

const EVerifyUsersFormMFA = ({
  isCorporateAdmin,
  usersData,
  signatoryIsSame,
  onNext,
  onSubmit,
}: EVerifyUsersFormMFAProps): JSX.Element => {
  const { previousPage } = useEnrollment();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const mfaApi = useMfaApi();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, register, errors, watch, clearErrors, setValue, reset } = useForm<FormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      adminUser: {
        emailAddress: usersData[0]?.emailAddress ?? '',
        firstName: usersData[0]?.firstName ?? '',
        middleInitial: usersData[0]?.middleInitial ?? '',
        lastName: usersData[0]?.lastName ?? '',
        phoneNumber: usersData[0]?.phoneNumber ?? '',
        phoneNumberExtension: usersData[0]?.phoneNumberExtension ?? '',
      },
      mouSignatoryIsSame: signatoryIsSame || false,
      mouSignatory: {
        emailAddress: usersData[1]?.emailAddress ?? '',
        firstName: usersData[1]?.firstName ?? '',
        middleInitial: usersData[1]?.middleInitial ?? '',
        lastName: usersData[1]?.lastName ?? '',
        phoneNumber: usersData[1]?.phoneNumber ?? '',
        phoneNumberExtension: usersData[1]?.phoneNumberExtension ?? '',
      },
    },
  });
  const { adminUser, mouSignatory, mouSignatoryIsSame } = watch();

  useEffect(() => {
    if (mouSignatoryIsSame) {
      (Object.keys(adminUser) as (keyof EVerifyUser)[]).forEach((fieldName) => {
        const nestedFieldName = `mouSignatory.${fieldName}`;
        if (adminUser[fieldName] !== mouSignatory[fieldName]) {
          setValue(nestedFieldName, adminUser[fieldName]);
        }
      });
    }
  }, [adminUser, mouSignatoryIsSame, mouSignatory, setValue]);

  const [showPageLoader, setShowPageLoader] = useState<boolean>(false);
  const [checkEmailStatus, setCheckEmailStatus] = useState<CheckEmailStatus>(CheckEmailStatus.NONE);
  const [prevEmail, setPrevEmail] = useState<string | undefined>(usersData[0]?.emailAddress);
  const [validEmail, setValidEmail] = useState(false);
  const [isExistingEmailModalOpen, setIsExistingEmailModalOpen] = useState(false);
  const allowAdminUserInput = validEmail && checkEmailStatus !== CheckEmailStatus.MFA;

  const handleContinue = () => {
    setIsExistingEmailModalOpen(false);
    setShowPageLoader(true);

    setErrorMessage('');
    setPrevEmail(adminUser.emailAddress);
    setValidEmail(true);

    onSubmit(
      {
        emailAddress: adminUser.emailAddress,
        firstName: '',
        middleInitial: '',
        lastName: '',
        phoneNumber: '',
        phoneNumberExtension: '',
        id: '',
        mouSignatory: 'No',
        admin: 'Yes',
      },
      mouSignatory,
      mouSignatoryIsSame,
    );
    onNext();
  };

  const handleCancel = () => {
    setValidEmail(false);
    setIsExistingEmailModalOpen(false);
    setShowPageLoader(false);

    setErrorMessage('');
    setPrevEmail(adminUser.emailAddress);
  };

  const handleOnNext = handleSubmit(async () => {
    if (validEmail) {
      onSubmit(adminUser, mouSignatory, mouSignatoryIsSame);
      onNext();
    }

    if (!validEmail) {
      try {
        // Start Loading
        setShowPageLoader(true);

        // Validate email
        const request: CheckEmailRequest = {
          checkEmailBody: {
            emailAddress: adminUser.emailAddress,
            app: Application.EVERIFY,
          },
        };
        const response = await mfaApi.checkEmail(request);

        // Set values from response
        setCheckEmailStatus(response.checkEmailStatus);

        // Reset form values if email was changed
        if (prevEmail !== adminUser.emailAddress) {
          reset({
            adminUser: {
              emailAddress: adminUser.emailAddress,
              firstName: '',
              middleInitial: '',
              lastName: '',
              phoneNumber: '',
              phoneNumberExtension: '',
            },
            mouSignatoryIsSame: false,
            mouSignatory: {
              emailAddress: '',
              firstName: '',
              middleInitial: '',
              lastName: '',
              phoneNumber: '',
              phoneNumberExtension: '',
            },
          });
        }

        // Corp admins skip user information input for MFA email
        if (isCorporateAdmin && response.checkEmailStatus === CheckEmailStatus.MFA) {
          setValidEmail(false);
          setIsExistingEmailModalOpen(true);
        } else {
          setValidEmail(true);
        }

        // Go to next screen
        setShowPageLoader(false);
        setErrorMessage('');
        setPrevEmail(adminUser.emailAddress);
      } catch {
        setErrorMessage(AlertConstants.EVERIFY_USERS_CHECK_EMAIL_FAIL);
        setShowPageLoader(false);
      }
    }
  });

  // Prevents page from refreshing on Enter key
  const handleOnEnter: KeyboardEventHandler = async (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await handleOnNext();
    }
  };

  const handleOnBack = () => {
    if (validEmail) {
      clearErrors();
      setValidEmail(false);
    } else {
      previousPage();
    }
  };
  return (
    <>
      <Form className="maxw-none" noValidate onKeyDown={(e) => handleOnEnter(e)}>
        {showPageLoader && <PageLoader label="Loading" sectionOverlay />}
        {errorMessage && (
          <Alert status="error" className="margin-top-2">
            <Alert.Text>{errorMessage}</Alert.Text>
          </Alert>
        )}
        <>
          <TextInputControl
            id="adminUser-emailAddress"
            name="adminUser.emailAddress"
            className="grid-col-12 tablet:grid-col-6 desktop:grid-col-4"
            label={validEmail ? '' : 'Email Address'}
            hidden={validEmail}
            type="email"
            required
            ref={register({
              required: {
                value: !validEmail,
                message: 'This field is required.',
              },
              pattern: {
                value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
                message: 'This field must be formatted as an email address.',
              },
            })}
            errorId={errors.adminUser?.emailAddress && 'emailError'}
            errorMessage={errors.adminUser?.emailAddress?.message}
          />
          <ExistingEverifyUserModal
            when={isExistingEmailModalOpen}
            onContinue={handleContinue}
            onCancel={handleCancel}
          />
          {validEmail && (
            <dl className="margin-bottom-0">
              <FieldGroup.Field name="Email Address">{adminUser.emailAddress || '---'}</FieldGroup.Field>
            </dl>
          )}
          {validEmail && checkEmailStatus === CheckEmailStatus.MFA && (
            <Alert status="info" slim className="margin-y-3">
              <Alert.Text>{AlertConstants.EVERIFY_USERS_CHECK_EMAIL_MFA}</Alert.Text>
            </Alert>
          )}
          {allowAdminUserInput && (
            <>
              <TextInputControl
                id="adminUser-firstName"
                name="adminUser.firstName"
                className="grid-col-12 tablet:grid-col-6 desktop:grid-col-4"
                label="First Name"
                required
                ref={register({
                  required: {
                    value: allowAdminUserInput,
                    message: 'This field is required.',
                  },
                  maxLength: {
                    value: 25,
                    message: 'This field must not exceed 25 characters.',
                  },
                  pattern: {
                    value: /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/,
                    message: 'This field must not contain numbers or special characters.',
                  },
                })}
                errorId={errors.adminUser?.firstName && 'firstNameError'}
                errorMessage={errors.adminUser?.firstName?.message}
              />
              <TextInputControl
                id="adminUser-middleInitial"
                name="adminUser.middleInitial"
                className="grid-col-6 tablet:grid-col-3 desktop:grid-col-2"
                label="Middle Initial"
                ref={register({
                  maxLength: {
                    value: 1,
                    message: 'This field must not exceed 1 character.',
                  },
                  pattern: {
                    value: /^[a-zA-Z]+$/,
                    message: 'This field must not contain numbers or special characters.',
                  },
                })}
                errorId={errors.adminUser?.middleInitial && 'middleInitialError'}
                errorMessage={errors.adminUser?.middleInitial?.message}
              />
              <TextInputControl
                id="adminUser-lastName"
                name="adminUser.lastName"
                className="grid-col-12 tablet:grid-col-6 desktop:grid-col-4"
                label="Last Name"
                required
                ref={register({
                  required: {
                    value: allowAdminUserInput,
                    message: 'This field is required.',
                  },
                  maxLength: {
                    value: 25,
                    message: 'This field must not exceed 25 characters.',
                  },
                  pattern: {
                    value: /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/,
                    message: 'This field must not contain numbers or special characters.',
                  },
                })}
                errorId={errors.adminUser?.lastName && 'lastNameError'}
                errorMessage={errors.adminUser?.lastName?.message}
              />
              <TextInputControl
                id="adminUser-phoneNumber"
                name="adminUser.phoneNumber"
                className="grid-col-12 tablet:grid-col-6 desktop:grid-col-3"
                label="Phone Number"
                required
                hint="10 digits, numbers only"
                ref={register({
                  required: {
                    value: allowAdminUserInput,
                    message: 'This field is required.',
                  },
                  pattern: {
                    value: /^\d{10}$/,
                    message: 'This field must contain 10 digits.',
                  },
                  validate: (value: string) => {
                    if (!/^(?!0|1)/.exec(value)) {
                      return 'Please enter valid phone number without 0 or 1 as the first digit';
                    }
                    return true;
                  },
                })}
                errorId={errors.adminUser?.phoneNumber && 'phoneNumberError'}
                errorMessage={errors.adminUser?.phoneNumber?.message}
              />
              <TextInputControl
                id="adminUser-phoneNumberExtension"
                name="adminUser.phoneNumberExtension"
                className="grid-col-6 tablet:grid-col-3 desktop:grid-col-2"
                label="Phone Number Extension"
                hint="Up to 6 digits, numbers only"
                ref={register({
                  maxLength: {
                    value: 6,
                    message: 'This field must not exceed 6 digits.',
                  },
                  pattern: {
                    value: /^\d+$/,
                    message: 'This field must only contain digits.',
                  },
                })}
                errorId={errors.adminUser?.phoneNumberExtension && 'phoneNumberExtensionError'}
                errorMessage={errors.adminUser?.phoneNumberExtension?.message}
              />
            </>
          )}
          {!isCorporateAdmin && validEmail && (
            <>
              <h2>Signatory</h2>
              <p>
                The <strong>Signatory</strong> must have authority to sign the MOU on behalf of your company and will be
                added as the Point of Contact on the E-Verify account.
              </p>
              {allowAdminUserInput && (
                <Checkbox
                  id="mouSignatory-Is-Same"
                  className="margin-top-4"
                  name="mouSignatoryIsSame"
                  label="The Signatory is the same person as the Program Administrator."
                  ref={register()}
                  onChange={(event) => {
                    (Object.keys(mouSignatory) as (keyof EVerifyUser)[]).forEach((fieldName) => {
                      const nestedFieldName = `mouSignatory.${fieldName}`;
                      if (event.target.checked) {
                        clearErrors(nestedFieldName);
                      }
                    });
                  }}
                />
              )}
              <>
                <TextInputControl
                  id="mouSignatory-emailAddress"
                  name="mouSignatory.emailAddress"
                  className="grid-col-12 tablet:grid-col-6 desktop:grid-col-4"
                  label="Email Address"
                  disabled={mouSignatoryIsSame}
                  required
                  ref={register({
                    required: {
                      value: !mouSignatoryIsSame,
                      message: 'This field is required.',
                    },
                    pattern: {
                      value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
                      message: 'This field must be formatted as an email address.',
                    },
                  })}
                  errorId={errors.mouSignatory?.emailAddress && 'emailError'}
                  errorMessage={errors.mouSignatory?.emailAddress?.message}
                />
                <TextInputControl
                  id="mouSignatory-firstName"
                  name="mouSignatory.firstName"
                  className="grid-col-12 tablet:grid-col-6 desktop:grid-col-4"
                  label="First Name"
                  disabled={mouSignatoryIsSame}
                  required
                  ref={register({
                    required: {
                      value: !mouSignatoryIsSame,
                      message: 'This field is required.',
                    },
                    maxLength: {
                      value: 25,
                      message: 'This field must not exceed 25 characters.',
                    },
                    pattern: {
                      value: /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/,
                      message: 'This field must not contain numbers or special characters.',
                    },
                  })}
                  errorId={errors.mouSignatory?.firstName && 'firstNameError'}
                  errorMessage={errors.mouSignatory?.firstName?.message}
                />
                <TextInputControl
                  id="mouSignatory-middleInitial"
                  name="mouSignatory.middleInitial"
                  className="grid-col-6 tablet:grid-col-3 desktop:grid-col-2"
                  label="Middle Initial"
                  disabled={mouSignatoryIsSame}
                  ref={register({
                    maxLength: {
                      value: 1,
                      message: 'This field must not exceed 1 character.',
                    },
                    pattern: {
                      value: /^[a-zA-Z]+$/,
                      message: 'This field must not contain numbers or special characters.',
                    },
                  })}
                  errorId={errors.mouSignatory?.middleInitial && 'middleInitialError'}
                  errorMessage={errors.mouSignatory?.middleInitial?.message}
                />
                <TextInputControl
                  id="mouSignatory-lastName"
                  name="mouSignatory.lastName"
                  className="grid-col-12 tablet:grid-col-6 desktop:grid-col-4"
                  label="Last Name"
                  disabled={mouSignatoryIsSame}
                  required
                  ref={register({
                    required: {
                      value: !mouSignatoryIsSame,
                      message: 'This field is required.',
                    },
                    maxLength: {
                      value: 25,
                      message: 'This field must not exceed 25 characters.',
                    },
                    pattern: {
                      value: /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/,
                      message: 'This field must not contain numbers or special characters.',
                    },
                  })}
                  errorId={errors.mouSignatory?.lastName && 'lastNameError'}
                  errorMessage={errors.mouSignatory?.lastName?.message}
                />
                <TextInputControl
                  id="mouSignatory-phoneNumber"
                  name="mouSignatory.phoneNumber"
                  className="grid-col-12 tablet:grid-col-6 desktop:grid-col-3"
                  label="Phone Number"
                  hint="10 digits, numbers only"
                  disabled={mouSignatoryIsSame}
                  required
                  ref={register({
                    required: {
                      value: !mouSignatoryIsSame,
                      message: 'This field is required.',
                    },
                    pattern: {
                      value: /^\d{10}$/,
                      message: 'This field must contain 10 digits.',
                    },
                    validate: (value: string) => {
                      if (!/^(?!0|1)/.exec(value)) {
                        return 'Please enter valid phone number without 0 or 1 as the first digit';
                      }
                      return true;
                    },
                  })}
                  errorId={errors.mouSignatory?.phoneNumber && 'phoneNumberError'}
                  errorMessage={errors.mouSignatory?.phoneNumber?.message}
                />
                <TextInputControl
                  id="mouSignatory-phoneNumberExtension"
                  name="mouSignatory.phoneNumberExtension"
                  className="grid-col-6 tablet:grid-col-3 desktop:grid-col-2"
                  label="Phone Number Extension"
                  disabled={mouSignatoryIsSame}
                  hint="Up to 6 digits, numbers only"
                  ref={register({
                    maxLength: {
                      value: 6,
                      message: 'This field must not exceed 6 digits.',
                    },
                    pattern: {
                      value: /^\d+$/,
                      message: 'This field must only contain digits.',
                    },
                  })}
                  errorId={errors.mouSignatory?.phoneNumberExtension && 'phoneNumberExtensionError'}
                  errorMessage={errors.mouSignatory?.phoneNumberExtension?.message}
                />
              </>
            </>
          )}
        </>
      </Form>
      <hr className="margin-top-5 margin-bottom-0" />
      <EnrollmentStepperButtons
        nextButtonType="submit"
        onNext={handleOnNext}
        onBack={handleOnBack}
        showNextOnEdit={!validEmail}
        noDivider
      />
    </>
  );
};

export default EVerifyUsersFormMFA;
